import * as React from "react";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { InputFieldState } from "./InputFieldState";

export interface TextInputProps {
  label: string;
  state: InputFieldState;
  onChange?: (value: string, event: ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (value: string, event: React.KeyboardEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  errorMessage?: string;
  editable?: boolean;
  onEnterPressed?: () => void;
  className?: string;
  focus?: boolean;
  disabled?: boolean;
  autocomplete?: string;
  children?: React.ReactNode;
  type?: string;
}

const TextInput: React.FC<TextInputProps> = ({
  label = "Label",
  state,
  onChange,
  onKeyDown,
  onFocus,
  errorMessage,
  editable,
  onEnterPressed,
  className,
  focus,
  disabled,
  autocomplete = 'none',
  type = 'text',
  children
}) => {
  const [focused, setFocused] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (focus) {
      inputRef.current?.focus();
    }
  }, [focus]);

  const id = "input" + Math.random().toString(36).substring(2, 10);

  // Label is active (floating) if:
  // 1. The input is focused
  // 2. The input has a value
  // 3. The placeholder of the specific input is managed externally and passed as value (rare)
  const isLabelActive = focused || (state.value && state.value.toString().length > 0);

  const classLabel = "label " + (isLabelActive ? "active" : '');
  const inputClassName = "react-input " + (className ? className + ' ' : '');

  return (
    <div className="input-field">
      <input
        disabled={disabled}
        ref={inputRef}
        id={id}
        readOnly={editable === false}
        autoComplete={autocomplete}
        onChange={(event) => onChange?.(event.target.value, event)}
        onKeyDown={(event) => {
          if (onEnterPressed && event.keyCode === 13) {
            onEnterPressed();
          }
          onKeyDown?.(event.currentTarget.value, event);
        }}
        onFocus={(e) => {
          setFocused(true);
          onFocus?.(e);
        }}
        onBlur={(e) => {
          setFocused(false);
          onFocus?.(e);
        }}
        value={state.value}
        type={type}
        className={inputClassName + (state.errorMessage ? 'invalid' : '')}
      />
      <label
        onClick={() => {
          inputRef.current?.focus();
        }}
        htmlFor={id}
        className={classLabel}
      >
        {label}
      </label>
      {children}
      <div className="error_message">{state.errorMessage}</div>
    </div>
  );
};

export default TextInput;